import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BrandingProvider, useBranding } from './contexts/ThemeContext';

import AppRoutes from './components/routing/AppRoutes';
import ErrorFallback from './components/common/ErrorFallback';
import EnhancedAiraAI from './components/ai/AiraAI-Enhanced';
import NotificationPermissionPrompt from './components/notifications/NotificationPermissionPrompt';

import './styles/global.css';

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered:', registration);
      })
      .catch(error => {
        console.log('SW registration failed:', error);
      });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function ThemedApp() {
  const { theme, branding } = useBranding();

  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor);
    const pc = branding.primaryColor || '#C0EB38';
    const r = parseInt(pc.slice(1,3),16), g = parseInt(pc.slice(3,5),16), b = parseInt(pc.slice(5,7),16);
    root.style.setProperty('--brand-primary-hover', `rgb(${Math.max(0,r-20)},${Math.max(0,g-20)},${Math.max(0,b-20)})`);
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
    root.style.setProperty('--brand-sidebar', branding.sidebarColor);
    root.style.setProperty('--brand-sidebar-text', branding.sidebarTextColor);
    root.style.setProperty('--brand-header', branding.headerColor);
    root.style.setProperty('--brand-header-text', branding.headerTextColor);
  }, [branding]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <SocketProvider>
              <div className="App">
                <AppRoutes />
                <EnhancedAiraAI />
                <NotificationPermissionPrompt />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#1A1D1F',
                      color: '#fff',
                      borderRadius: '16px',
                      padding: '12px 16px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                      fontSize: '14px',
                      fontWeight: 500,
                    },
                    success: {
                      style: {
                        background: '#1A1D1F',
                        border: `1px solid ${branding.primaryColor}`,
                      },
                      iconTheme: {
                        primary: branding.primaryColor,
                        secondary: '#1A1D1F',
                      },
                    },
                    error: {
                      style: {
                        background: '#1A1D1F',
                        border: '1px solid #EF4444',
                      },
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: '#1A1D1F',
                      },
                    },
                  }}
                />
              </div>
            </SocketProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary fallback={<ErrorFallback error={new Error('Something went wrong')} resetErrorBoundary={() => window.location.reload()} />}>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <ThemedApp />
        </BrandingProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
